import { useQuery } from "@rocicorp/zero/react";
import { styled } from "goober";
import { useEffect, useEffectEvent, useState } from "react";
import { queries } from "../../../shared/queries";
import { pushRequestReviewers } from "../../lib/pushRequestReviewers";
import { userDisplayName } from "../../lib/userDisplayName";
import { Avatar } from "../Avatar";
import { Button } from "../Button";
import { Icon } from "../Icon";
import { EmptyState } from "../EmptyState";
import { Keycap } from "../Keycap";
import {
  DropdownRowContent,
  DropdownSections,
  RowButton,
  SearchableDropdown,
} from "../SearchableDropdown";

const Footer = styled("div")`
  position: absolute;
  inset: auto 0 0;
  padding: 24px 8px 8px;
  background: linear-gradient(transparent, var(--surface-primary) 24px);
  pointer-events: none;

  & > button {
    width: 100%;
    justify-content: center;
    border-radius: 9999px;
    pointer-events: auto;
  }
`;

const FooterClearance = styled("div")`
  height: 48px;
`;

const SendLabel = styled("span")`
  display: flex;
  align-items: center;
  gap: 8px;
  --keycap-bg: color-mix(in srgb, var(--control-fg) 16%, transparent);
  --keycap-fg: var(--control-fg);
`;

function SendFooter({ count, onSend }: { count: number; onSend: () => void }) {
  const sendFromKeyboard = useEffectEvent(() => {
    if (count > 0) onSend();
  });
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (
        event.key !== "Enter" ||
        event.isComposing ||
        event.defaultPrevented ||
        (event.target instanceof Element &&
          event.target.closest("[data-nav-row]"))
      )
        return;
      event.preventDefault();
      sendFromKeyboard();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);
  return (
    <Footer>
      <Button size="l" disabled={count === 0} onClick={onSend}>
        <SendLabel>
          {count === 1 ? "Send to 1 person" : `Send to ${count} people`}
          <Keycap as="span" aria-hidden>
            <Icon name="corner-down-left" size={12} />
          </Keycap>
        </SendLabel>
      </Button>
    </Footer>
  );
}

type Props = {
  spaceId: string;
  workflowId: string;
  excludedUserIds: ReadonlySet<string>;
  disabled: boolean;
  opener: React.ReactNode;
  openRef: React.RefObject<(() => void) | null>;
  onSend: (reviewerIds: string[]) => void;
};

export function PushRequestPicker({
  spaceId,
  workflowId,
  excludedUserIds,
  disabled,
  opener,
  openRef,
  onSend,
}: Props) {
  const [spaceGrants, spaceGrantsDetails] = useQuery(
    queries.spaceMembers({ spaceId })
  );
  const [mainBranch] = useQuery(queries.recentWorkflowPushers({ workflowId }));
  const [now, setNow] = useState(() => Date.now());
  const [picked, setPicked] = useState<ReadonlySet<string> | null>(null);
  const { reviewers, suggestedIds, defaultIds } = pushRequestReviewers({
    spaceGrants: spaceGrants ?? [],
    mainCommits: mainBranch?.commits ?? [],
    excludedUserIds,
    now,
  });
  const selected = picked ?? new Set(defaultIds);
  const selectedIds = reviewers.flatMap((u) =>
    selected.has(u.id) ? [u.id] : []
  );

  const handleOpenChange = (open: boolean) => {
    if (!open) return;
    setPicked(null);
    setNow(Date.now());
  };
  const toggle = (userId: string) => {
    const next = new Set(selected);
    if (!next.delete(userId)) next.add(userId);
    setPicked(next);
  };

  return (
    <SearchableDropdown
      opener={opener}
      openRef={openRef}
      disabled={disabled}
      align="right"
      searchable={reviewers.length > 0}
      searchPlaceholder="Search people…"
      height={360}
      aria-label="Send push request"
      onOpenChange={handleOpenChange}
      footer={(close) => (
        <SendFooter
          count={selectedIds.length}
          onSend={() => {
            close();
            onSend(selectedIds);
          }}
        />
      )}
    >
      {(query) => {
        const q = query.trim().toLowerCase();
        const rows = (suggested: boolean) =>
          reviewers
            .filter(
              (u) =>
                suggestedIds.has(u.id) === suggested &&
                (!q ||
                  u.name.toLowerCase().includes(q) ||
                  u.email.toLowerCase().includes(q))
            )
            .map((u) => (
              <RowButton
                key={u.id}
                aria-pressed={selected.has(u.id)}
                onClick={() => toggle(u.id)}
              >
                <DropdownRowContent
                  icon={<Avatar user={u} size={16} tooltip={false} />}
                  name={userDisplayName(u)}
                  secondary={u.name && u.name !== u.email ? u.email : undefined}
                  trailing={
                    selected.has(u.id) && (
                      <Icon
                        name="check"
                        size={12}
                        color="var(--text-secondary)"
                      />
                    )
                  }
                />
              </RowButton>
            ));
        return (
          <>
            <DropdownSections
              sections={[
                { label: "Recently pushed this live", rows: rows(true) },
                { label: "Can approve in this space", rows: rows(false) },
              ]}
              empty={
                spaceGrantsDetails.type === "complete" && (
                  <EmptyState
                    iconName={q ? "text-search" : "users-round"}
                    iconSize={24}
                    title={
                      q ? "No people found" : "Nobody else can approve this"
                    }
                  />
                )
              }
            />
            <FooterClearance />
          </>
        );
      }}
    </SearchableDropdown>
  );
}
